import { Box, FormControlLabel, Switch, Typography } from "@material-ui/core";
import { Alert, Skeleton } from "@material-ui/lab";
import { extent } from "d3-array";
import { scaleLinear } from "d3-scale";
import { interpolateSpectral } from "d3-scale-chromatic";
import { useMemo, useState } from "react";
import useFetchGraphData from "../../../hooks/useFetchGraphData";
import DepthHeatmapChart from "./DepthHeatmapChart";

// Default dynamic scale renders high values as red (standard "high pollutant = bad" convention).
// Add parameter IDs here only for the rare cases where LOW values indicate poor conditions,
// so the scale inverts and low values render red instead.
const LOW_IS_BAD_PARAMS = new Set([
  10, // Light Transmittance 99% attenuation depth (shallow = murky)
  680, // Dissolved Oxygen, % saturation
]);

const STATIC_SCALES = {
  3: {
    // Dissolved Oxygen
    breaks: [0, 5, 6, 7, 8, 9, 10, 11],
    colors: [
      "#e6665c",
      "#ff9b93",
      "#f6fbb5",
      "#e1f3aa",
      "#addeac",
      "#5ca7ba",
      "#6869af",
      "#4e65ac",
    ],
    rationale:
      "Regulation 38 Aquatic Life Warm 1 standard recommends dissolved oxygen >5.0 mg/L. Lower values can lead to hypoxia and harm aquatic life.",
  },
  8: {
    // Temperature - concentrated in 18-26 range where most data falls
    // 26.2 = chronic standard, 29.3 = acute standard (Reg 38 Aquatic Life 2)
    breaks: [0, 8, 13, 16, 19, 21, 22, 23, 24, 26.2],
    colors: [
      "#4e65ac",
      "#6869af",
      "#5ca7ba",
      "#7ecba7",
      "#addeac",
      "#f6fbb5",
      "#ff9b93",
      "#e6665c",
      "#c43a2a",
      "#8b1a0e",
    ],
    rationale:
      "Regulation 38 Aquatic Life 2 standards specify chronic (<26.2°C) and acute (<29.3°C) thresholds. Elevated temperatures can reduce oxygen levels and stress aquatic species.",
  },
  7: {
    // pH – Outside of range is bad
    breaks: [5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10],
    colors: [
      "#d45c53",
      "#ff9b93",
      "#f6c5a0",
      "#addeac",
      "#4eaa64",
      "#4eaa64",
      "#addeac",
      "#f6c5a0",
      "#ff9b93",
      "#d45c53",
    ],
    rationale:
      "Aquatic life thrives in the pH range 6.5–9.0. Values outside this range may cause physiological stress or mortality.",
  },
  24: {
    // Chlorophyll-a (ug/L)
    breaks: [0, 12, 18, 40],
    colors: ["#4e65ac", "#addeac", "#f6fbb5", "#ff9b93"],
    rationale:
      "Regulation 38 standard recommends chlorophyll-a ≤18 µg/L. Elevated levels indicate algal blooms and eutrophication risk.",
  },
  28: {
    // Total Phosphorus (ug/L)
    breaks: [0, 100, 170, 250],
    colors: ["#4e65ac", "#addeac", "#f6fbb5", "#ff9b93"],
    rationale:
      "The interim Regulation 31 nutrient criteria for total phosphorus in streams is 170 µg/L. Exceedance indicates potential for algal growth.",
  },
  29: {
    // Total Nitrogen as N (ug/L) - more color variability with 0-2000 range
    breaks: [0, 250, 500, 750, 1000, 1500, 2000],
    colors: [
      "#4e65ac",
      "#6869af",
      "#5ca7ba",
      "#addeac",
      "#f6fbb5",
      "#ff9b93",
      "#e6665c",
    ],
    rationale:
      "The interim Regulation 31 nutrient criteria for total nitrogen in streams is 2,010 µg/L. Higher levels can drive eutrophication.",
  },
  30: {
    // Total Suspended Solids (mg/L)
    breaks: [0, 10, 25, 50],
    colors: ["#4e65ac", "#addeac", "#f6fbb5", "#ff9b93"],
    rationale:
      "Natural streams typically have 0–25 mg/L. Values >50 mg/L may indicate urban runoff, erosion, or turbidity impacts on aquatic life.",
  },
  1902: {
    // Conductivity (uS/cm) - more color variability with 0-1500 range
    breaks: [0, 250, 500, 750, 1000, 1250, 1500],
    colors: [
      "#4e65ac",
      "#6869af",
      "#5ca7ba",
      "#addeac",
      "#f6fbb5",
      "#ff9b93",
      "#e6665c",
    ],
    rationale:
      "EPA suggests conductivity >1500 µS/cm is elevated for streams. Recommended level for drinking water is <1000 µS/cm.",
  },
  6: {
    // Secchi Depth (m) — Low is bad
    breaks: [0, 0.5, 2, 4, 5],
    colors: ["#e6665c", "#ff9b93", "#f6fbb5", "#addeac", "#4e65ac"],
    rationale:
      "Secchi depth is a measure of water clarity. Values <2 meters may indicate high algal biomass or suspended solids.",
  },
  100139: {
    // TDN:TDP Ratio
    breaks: [0, 3.5, 7.12, 10, 15, 20, 25],
    colors: [
      "#d45c53",
      "#ff9b93",
      "#f6fbb5",
      "#e1f3aa",
      "#addeac",
      "#5ca7ba",
      "#4e65ac",
    ],
    rationale:
      "The TDN:TDP ratio is used to assess nitrogen vs phosphorus limitation. Ratios below ~7 suggest nitrogen limitation; above ~20 suggest phosphorus limitation.",
  },
  100138: {
    // TN:TP Ratio
    breaks: [0, 3.5, 7.12, 10, 15, 20, 25],
    colors: [
      "#d45c53",
      "#ff9b93",
      "#f6fbb5",
      "#e1f3aa",
      "#addeac",
      "#5ca7ba",
      "#4e65ac",
    ],
    rationale:
      "The TN:TP ratio helps identify nutrient limitation. Low ratios suggest nitrogen limitation, while high ratios suggest phosphorus limitation.",
  },
  100140: {
    // TIN:SRP Ratio
    breaks: [0, 3.5, 7.12, 10, 15, 20, 25],
    colors: [
      "#d45c53",
      "#ff9b93",
      "#f6fbb5",
      "#e1f3aa",
      "#addeac",
      "#5ca7ba",
      "#4e65ac",
    ],
    rationale:
      "The TIN:SRP ratio is used to assess nutrient limitation in dissolved forms. Lower ratios imply nitrogen limitation; higher ratios suggest phosphorus limitation.",
  },
};

function buildDynamicScale(values, lowIsBad = false) {
  const [min, max] = extent(values);
  const step = (max - min) / 6;
  const breaks = Array.from({ length: 6 }, (_, i) => min + step * i).concat(
    max
  );
  const scale = scaleLinear()
    .domain(breaks)
    .range(
      breaks.map((b) => {
        const t = (b - min) / (max - min);
        // Default: high values get red (spectral 0). If lowIsBad, low values get red instead.
        return interpolateSpectral(lowIsBad ? t : 1 - t);
      })
    );
  return { scale, breaks, colors: scale.range() };
}

export default function DepthHeatmapChartContainer({
  start_date,
  end_date,
  locationid,
  parameterid,
  height = 400,
  showLegend = true,
  title = null,
}) {
  const [showValues, setShowValues] = useState(true);
  const {
    data = [],
    isLoading,
    isError,
  } = useFetchGraphData({ start_date, end_date, locationid, parameterid }, [
    "depth-profile-graph-data",
  ]);

  const {
    colorScale,
    breaks,
    colors,
    dates,
    matrix,
    unit,
    isDynamicScale,
    rationale,
  } = useMemo(() => {
    if (!data.length)
      return {
        colorScale: () => "#eee",
        breaks: [],
        colors: [],
        dates: [],
        matrix: [],
        unit: "",
      };

    const depths = [...new Set(data.map((d) => d.depth_value))].sort(
      (a, b) => a - b
    );
    const uniqueDates = [...new Set(data.map((d) => d.rdate))].sort();

    const matrix = depths.map((depth) => ({
      id: depth,
      depth,
      data: uniqueDates.map((date) => {
        const point = data.find(
          (r) => r.depth_value === depth && r.rdate === date
        );
        return { x: date, y: point?.rvalue_converted ?? null };
      }),
    }));

    const staticScale = STATIC_SCALES[parameterid];
    const rationale = staticScale?.rationale;
    const unit = data[0].unit_name;

    if (staticScale) {
      return {
        colorScale: scaleLinear()
          .domain(staticScale.breaks)
          .range(staticScale.colors),
        // .clamp(true),
        breaks: staticScale.breaks,
        colors: staticScale.colors,
        dates: uniqueDates,
        matrix,
        unit,
        isDynamicScale: false,
        rationale,
      };
    }

    const vals = data.map((d) => d.rvalue_converted).filter((v) => v != null);
    const lowIsBad = LOW_IS_BAD_PARAMS.has(parameterid);
    const dyn = buildDynamicScale(vals, lowIsBad);

    return {
      colorScale: dyn.scale,
      breaks: dyn.breaks,
      colors: dyn.colors,
      dates: uniqueDates,
      matrix,
      unit,
      isDynamicScale: true,
    };
  }, [data, parameterid]);

  if (isLoading)
    return (
      <Skeleton variant="rect" height={height} width="100%" animation="wave" />
    );

  if (isError || !matrix.length)
    return (
      <Box
        height={height}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        No data for current selections
      </Box>
    );

  return (
    <>
      {title && (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h5" component="h2">
            {title}
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={showValues}
                onChange={(e) => setShowValues(e.target.checked)}
                color="primary"
              />
            }
            label="Show values in cells"
          />
        </Box>
      )}
      {!title && (
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <FormControlLabel
            control={
              <Switch
                checked={showValues}
                onChange={(e) => setShowValues(e.target.checked)}
                color="primary"
              />
            }
            label="Show values in cells"
          />
        </Box>
      )}
      <DepthHeatmapChart
        matrix={matrix}
        dates={dates}
        colorScale={colorScale}
        breaks={showLegend ? breaks : []}
        colors={showLegend ? colors : []}
        unit={unit}
        height={height}
        showValues={showValues}
      />
      {(isDynamicScale || rationale) && (
        <Box mt={2} width="100%">
          <Alert severity="info">
            {isDynamicScale ? (
              <>
                The color scale was generated dynamically based on the minimum
                and maximum values in your selected date range. It may change
                depending on your filter selections.
              </>
            ) : (
              <>{rationale}</>
            )}
          </Alert>
        </Box>
      )}
    </>
  );
}
